import { useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { DriveProvider } from "@/context/DriveContext";
import { BadgeCheck, BarChart3, Clapperboard, CreditCard, Crown, Gauge, HeartPulse, LayoutGrid, LogOut, MessageSquare, Recycle, Settings, Star, Users, Wrench } from "lucide-react";
import { HealBoundary } from "@/components/selfheal/HealBoundary";
import { SelfHealBanner } from "@/components/selfheal/SelfHealBanner";
import { CapacityBanner } from "@/components/capacity/CapacityBanner";
import { UnreadBadge, useUnread } from "@/components/messages/unread";
import { useAuth } from "@/context/AuthContext";
import { useSubscriptions } from "@/lib/subscriptions";
import { useI18n } from "@/i18n/I18nContext";
import { FeeChangeBanner } from "@/components/fees/FeeModelCard";

const TABS = [
  { to: "/admin", end: true, icon: LayoutGrid, label: "adminNav.apps", id: "dashboard" },
  { to: "/admin/abonnements", icon: CreditCard, label: "subs.title", id: "subscriptions", subs: true },
  { to: "/admin/statistiques", icon: BarChart3, label: "stats.title", id: "stats" },
  { to: "/admin/messages", icon: MessageSquare, label: "msg.title", id: "messages", badge: true },
  { to: "/admin/developpeurs", icon: Users, label: "adminNav.developers", id: "developers" },
  { to: "/admin/vip", icon: Crown, label: "adminNav.vip", id: "vip" },
  { to: "/admin/avis", icon: Star, label: "adminNav.reviews", id: "reviews" },
  { to: "/admin/capacite", icon: Gauge, label: "cap.title", id: "capacity" },
  { to: "/admin/purge", icon: Recycle, label: "purge.title", id: "purge" },
  { to: "/admin/sante", icon: HeartPulse, label: "heal.title", id: "health" },
  { to: "/admin/kit-pub", icon: Clapperboard, label: "promo.title", id: "promo" },
  { to: "/admin/atelier", icon: Wrench, label: "atelier.title", id: "atelier" },
  { to: "/admin/verification-android", icon: BadgeCheck, label: "adminNav.android", id: "android" },
  { to: "/admin/parametres", icon: Settings, label: "adminNav.settings", id: "settings" },
];

const framed = "border-2 border-[var(--gold)] shadow-[0_0_12px_rgba(245,197,66,0.25)]";
const tabClass = (extra) => ({ isActive }) =>
  `${extra} inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors duration-150 ${
    isActive ? "bg-[var(--gold)] text-black" : "text-neutral-200 hover:bg-white/5 hover:text-[var(--gold-bright)]"}`;

export const AdminShell = () => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const unread = useUnread("/admin/messages/unread");
  const subs = useSubscriptions();
  const { pathname } = useLocation();
  useEffect(() => {
    document.querySelector('[data-testid="admin-nav"] [aria-current="page"]')?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [pathname]);
  const out = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };
  return (
    <div data-testid="admin-shell">
      <div className="border-b border-[rgba(245,197,66,0.18)] bg-[rgba(74,6,11,0.55)]">
        <div className="mx-auto max-w-7xl px-4 pt-2.5 sm:px-8">
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <p className="min-w-0 break-words font-display text-xs font-bold uppercase tracking-widest text-[var(--gold-bright)]">{t("adminNav.hub")}</p>
            <div className="flex min-w-0 items-center gap-3">
              <span className="hidden truncate text-xs text-neutral-300 sm:inline" data-testid="admin-user-email">{user?.email}</span>
              <button type="button" onClick={out} className="btn-red btn-sm shrink-0" data-testid="admin-logout-btn">
                <LogOut className="h-4 w-4 rtl:-scale-x-100" /> {t("admin.logout")}
              </button>
            </div>
          </div>
          <nav aria-label={t("adminNav.hub")} className="-mx-1 mt-2 flex gap-1 overflow-x-auto px-1 pb-2.5 [scrollbar-width:thin] lg:flex-wrap lg:overflow-visible" data-testid="admin-nav">
            {TABS.map(({ to, end, icon: Icon, label, id, badge, subs: live }) => (
              <NavLink key={id} to={to} end={end} className={tabClass(live ? framed : "border-2 border-transparent")} data-testid={`admin-nav-${id}`}>
                <Icon className="h-4 w-4" /> {t(label)} {badge && <UnreadBadge count={unread} testId="admin-messages-unread" />}
                {live && subs && <span className="ms-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[0.65rem] font-bold text-white" data-testid="admin-subs-badge">{subs.counts.paid_total}</span>}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <CapacityBanner />
      <SelfHealBanner />
      <HealBoundary name="admin-section" resetKey={pathname}>
        <div className="mx-auto max-w-7xl px-4 empty:hidden sm:px-8"><FeeChangeBanner /></div>
        <DriveProvider><Outlet /></DriveProvider>
      </HealBoundary>
    </div>
  );
};
